import type { Stripe } from '@stripe/stripe-js';
import { confirm3dsPayment, cancel3dsPayment, type CreateReservationResponse, type Confirm3dsResponse } from '@features/booking/api/reservations';

export type ThreeDsErrorCode = 'PAYMENT_3DS_CANCELLED' | 'PAYMENT_3DS_FAILED' | 'PAYMENT_3DS_CONFIRM_ERROR';

export class ThreeDsError extends Error {
  code: ThreeDsErrorCode;

  constructor(code: ThreeDsErrorCode, message: string) {
    super(message);
    this.name = 'ThreeDsError';
    this.code = code;
  }
}

export interface Handle3dsResult {
  confirmed: true;
  reservationId: string;
}

/**
 * Handle a 3DS challenge if the backend response indicates one is needed.
 * Returns null if no 3DS was required (normal flow).
 * Returns the confirmed reservation data if 3DS succeeded.
 * Throws on 3DS failure, cancellation, or confirm error.
 *
 * On failure: cancels the pending reservation in the backend to release
 * the seat immediately instead of waiting for the cleanup cron.
 */
export async function handle3dsChallenge(
  response: CreateReservationResponse,
  stripePromise: Promise<Stripe | null>,
): Promise<Handle3dsResult | null> {
  if (!response.requires_action) {
    return null;
  }
  // requires_action=true without client_secret is a malformed BE response.
  // Falling through would tell the caller "no 3DS needed" while the BE has
  // the reservation in pending_payment — silent payment death until the
  // cleanup cron. Throw so the catch handler shows an error and the user
  // can recover.
  if (!response.client_secret) {
    throw new ThreeDsError(
      'PAYMENT_3DS_FAILED',
      'Missing client_secret for 3DS challenge',
    );
  }

  const stripe = await stripePromise;
  if (!stripe) {
    throw new ThreeDsError('PAYMENT_3DS_FAILED', 'Stripe not loaded');
  }

  if (!response.reservation_id || !response.payment_intent_id) {
    throw new ThreeDsError('PAYMENT_3DS_FAILED', 'Missing reservation_id or payment_intent_id for 3DS confirmation');
  }

  const { error } = await stripe.handleNextAction({
    clientSecret: response.client_secret,
  });

  if (error) {
    // Cancel the pending reservation to release the seat immediately
    cancel3dsPayment(response.reservation_id).catch(() => {
      // Non-blocking: cleanup cron handles it if this fails
    });

    const code: ThreeDsErrorCode = error.code === 'canceled'
      ? 'PAYMENT_3DS_CANCELLED'
      : 'PAYMENT_3DS_FAILED';
    throw new ThreeDsError(code, error.message || 'Card verification failed');
  }

  try {
    const confirmResponse: Confirm3dsResponse = await confirm3dsPayment(
      response.reservation_id,
      response.payment_intent_id,
    );

    return {
      confirmed: true,
      reservationId: String(confirmResponse.reservationId),
    };
  } catch (confirmErr) {
    throw new ThreeDsError(
      'PAYMENT_3DS_CONFIRM_ERROR',
      confirmErr instanceof Error ? confirmErr.message : 'Confirmation failed',
    );
  }
}
